import axios from 'axios';
import { currentBearer } from '@/router/sessionBearer';
import { applyStorefrontRequestTimeout } from '@/router/storefrontRequestTimeout';
import { applyStorefrontPreviewParam } from '@/router/storefrontPreviewRequest';

const axiosInstance = axios.create({
  baseURL: process.env.API_BASE_URL,
  headers: {
    'Content-Type': 'application/vnd.api+json',
  }
});

// SAFRS endpoint mapping: jsonapi-vuex uses CamelCase type names from _jv.type
// to construct URLs (e.g. /Product/1), but SAFRS only accepts lowercase
// pluralized table names (e.g. /products/1/). This interceptor rewrites URLs.
const SAFRS_TYPE_TO_ENDPOINT = {
  'Account': 'accounts',
  'Batch': 'batches',
  'BatchOrder': 'batch_orders',
  // Campaign was renamed to Store (PR-5a) and the SAFRS surface flipped to
  // /stores/ (PR-5b). /campaigns still exists as a 308 redirect to /stores/,
  // but browsers refuse to follow a redirect on a CORS preflight -- so any
  // Campaign-typed jv op that PREFLIGHTS (PATCH/DELETE by id) died with
  // ERR_FAILED. Route Campaign-typed URLs straight to the real /stores/
  // endpoint so no redirect is ever involved (#1868). GET-by-id also
  // benefits: it hits the endpoint directly instead of relying on the
  // browser to follow the 308.
  'Campaign': 'stores',
  'CampaignProduct': 'campaign_products',
  'CreateTeammate': 'create_teammates',
  'Distributor': 'distributors',
  'Event': 'events',
  'Location': 'locations',
  'Order': 'orders',
  'OrderItem': 'order_items',
  'OrderNote': 'order_notes',
  'Product': 'products',
  'ProductAttribute': 'product_attributes',
  'ProductAttributeValue': 'product_attributes_values',
  'ProductType': 'product_types',
  'Role': 'roles',
  'StripeEvent': 'stripe_events',
  'Token': 'tokens',
  'Vendor': 'vendors',
};

// Interceptor to add auth token and prevent double /api/api paths
axiosInstance.interceptors.request.use(function (config) {
  // currentBearer() reads localStorage first and, for an anonymous storefront
  // session only, falls back to the in-memory copy when the browser has
  // dropped the storage key (router/sessionBearer.js).
  const token = currentBearer();
  if (token) {
    // Ensure headers object exists
    if (!config.headers) {
      config.headers = {};
    }

    // Set the Authorization header
    config.headers.Authorization = 'Bearer ' + token;
  }

  // Fix double /api/api in URL
  if (config.url && config.url.includes('/api/api')) {
    config.url = config.url.replace('/api/api', '/api');
  }
  
  // Also fix if baseURL + url creates double /api
  if (config.baseURL && config.baseURL.endsWith('/api') && config.url && config.url.startsWith('/api')) {
    config.url = config.url.substring(4); // Remove the leading /api from url
  }

  // Rewrite jsonapi-vuex CamelCase URLs to SAFRS lowercase endpoints.
  // e.g. /Product/46 → /products/46/, /CampaignProduct/3 → /campaign_products/3/
  if (config.url) {
    const urlParts = config.url.split('/').filter(Boolean);
    if (urlParts.length >= 1) {
      const firstSegment = urlParts[0];
      const endpoint = SAFRS_TYPE_TO_ENDPOINT[firstSegment];
      if (endpoint) {
        urlParts[0] = endpoint;
        config.url = '/' + urlParts.join('/') + '/';
      }
    }
  }

  // ------------------------------------------------------------------
  // Multipart uploads: never let a boundary-less Content-Type reach the wire.
  //
  // This instance sets a hard default of ``application/vnd.api+json``. For a
  // FormData body that default is fatal: Flask sees a declared content type
  // with no ``boundary=`` parameter, parses no file part, and the upload
  // endpoint honestly rejects the request with
  // 400 "No file provided." (api/src/endpoints/upload.py). The browser
  // surfaces the aborted request as "timeout exceeded", which hides the real
  // cause -- the request is rejected in about a second, never timing out.
  //
  // Callers previously tried to opt out per-call with
  // ``headers: { 'Content-Type': undefined }``, but axios 1.x resolves that
  // to ``application/x-www-form-urlencoded`` -- also boundary-less, also
  // broken. Others passed a literal ``multipart/form-data`` with no boundary,
  // which is broken for the same reason: XHR only generates the boundary when
  // NO Content-Type header is set on the request.
  //
  // Deleting the header here is the single mechanism that fixes every
  // FormData caller at once. Never hardcode a boundary -- only the browser
  // can, because only it knows the one it used to encode the body.
  // ``false`` is the axios sentinel for "send no such header": it is omitted
  // from headers.toJSON(), so setRequestHeader is never called for it and the
  // browser is free to generate ``multipart/form-data; boundary=...``.
  // Plain ``delete`` is NOT enough -- transformRequest runs after every
  // interceptor and would re-add a default of
  // ``application/x-www-form-urlencoded`` to the now-absent header.
  if (config.data instanceof FormData && config.headers) {
    if (typeof config.headers.set === 'function') {
      config.headers.set('Content-Type', false);
    } else {
      config.headers['Content-Type'] = false;
      delete config.headers['content-type'];
    }
  }

  // Storefront pages never hang blank: reads and the session-establishing
  // POSTs get a ~20s timeout; writes and admin requests do not
  // (router/storefrontRequestTimeout.js has the scope and the error contract).
  applyStorefrontRequestTimeout(config);

  // A storefront PREVIEW page (the store editor's preview, ?preview=1) tells
  // the API it is a preview on every call, so the owner preview of a store
  // that is not live can read it (router/storefrontPreviewRequest.js).
  applyStorefrontPreviewParam(config);

  return config;
});

export default axiosInstance;
